import React, { useEffect } from "react";
import Navigation from "./Navigation";

const DesignProcess = () => {
  useEffect(() => {
    document.title = "Design Process - Andy Persaud Portfolio";
    window.scrollTo(0, 0);
  }, []);

  const processSteps = [
    {
      id: "discovery",
      number: "01",
      title: "Discovery & Research",
      description: "Understanding the business goals, user needs, and technical constraints",
      methods: [
        "Stakeholder interviews",
        "Competitive analysis", 
        "User research & surveys",
        "Analytics review",
        "Technical feasibility assessment"
      ],
      deliverables: [
        "Research insights report",
        "User personas",
        "Journey maps",
        "Problem statement",
        "Success metrics definition"
      ],
      timeframe: "1-2 weeks",
      icon: "🔍"
    },
    {
      id: "definition",
      number: "02", 
      title: "Problem Definition",
      description: "Synthesizing research into clear, actionable design challenges",
      methods: [
        "Affinity mapping",
        "How might we sessions",
        "Prioritization workshops",
        "Constraint mapping",
        "Goal alignment sessions"
      ],
      deliverables: [
        "Design brief",
        "User stories",
        "Feature prioritization",
        "Design principles",
        "Success criteria"
      ],
      timeframe: "3-5 days",
      icon: "🎯"
    },
    {
      id: "ideation",
      number: "03",
      title: "Ideation & Concept Development", 
      description: "Generating and refining solutions through collaborative exploration",
      methods: [
        "Design sprints",
        "Rapid sketching",
        "Mind mapping",
        "Crazy 8s exercises",
        "Concept validation"
      ],
      deliverables: [
        "Concept sketches",
        "User flow diagrams",
        "Information architecture",
        "Wireframes",
        "Design system foundations"
      ],
      timeframe: "1-2 weeks",
      icon: "💡"
    },
    {
      id: "prototyping",
      number: "04",
      title: "Prototyping & Testing",
      description: "Building and validating solutions with real users before development",
      methods: [
        "Low-fi wireframing",
        "High-fi prototyping",
        "Usability testing",
        "A/B testing",
        "Accessibility audits"
      ],
      deliverables: [
        "Interactive prototypes",
        "Usability test reports",
        "Design specifications",
        "Component library",
        "Handoff documentation"
      ],
      timeframe: "2-3 weeks",
      icon: "🛠️"
    },
    {
      id: "implementation",
      number: "05",
      title: "Implementation & Launch",
      description: "Collaborating with development teams to ensure design integrity",
      methods: [
        "Developer collaboration",
        "Design QA",
        "Responsive testing",
        "Performance optimization",
        "Launch preparation"
      ],
      deliverables: [
        "Final design assets",
        "Style guide",
        "Implementation notes",
        "QA checklist",
        "Launch strategy"
      ],
      timeframe: "2-4 weeks",
      icon: "🚀"
    },
    {
      id: "measurement",
      number: "06",
      title: "Measurement & Iteration",
      description: "Monitoring performance and continuously improving the experience",
      methods: [
        "Analytics monitoring",
        "User feedback collection",
        "Performance tracking",
        "Iterative improvements",
        "Long-term optimization"
      ],
      deliverables: [
        "Performance reports",
        "Optimization recommendations",
        "Future roadmap",
        "Lessons learned",
        "Success story documentation"
      ],
      timeframe: "Ongoing",
      icon: "📊"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-r from-blue-50 to-indigo-50 section-padding">
        <div className="container-narrow text-center">
          <h1 className="heading-hero text-gray-900 mb-6 animate-fade-in">
            My Design Process
          </h1>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed animate-slide-up">
            A human-centered, iterative approach that balances user needs with business goals 
            and technical constraints to create meaningful digital experiences.
          </p>
        </div>
      </section>

      {/* Design Philosophy */}
      <section className="section-padding bg-white">
        <div className="container-narrow">
          <h2 className="heading-section text-center mb-12">
            My Design Philosophy
          </h2>
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="card-professional p-8 text-center bg-blue-50 border border-blue-200">
              <div className="text-4xl mb-4">🧠</div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">User-Centered Thinking</h3>
              <p className="text-gray-600">
                Every design decision starts with understanding real user needs, behaviors, and pain points. 
                I believe in designing for humans, not just interfaces.
              </p>
            </div>
            
            <div className="card-professional p-8 text-center bg-green-50 border border-green-200">
              <div className="text-4xl mb-4">💼</div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Business-Aware Design</h3>
              <p className="text-gray-600">
                My marketing background helps me balance user needs with business objectives. 
                Great design should drive measurable results and ROI.
              </p>
            </div>
            
            <div className="card-professional p-8 text-center bg-purple-50 border border-purple-200">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Iterative Improvement</h3>
              <p className="text-gray-600">
                Design is never &quot;done.&quot; I embrace continuous testing, learning, and refinement 
                to create experiences that evolve with user needs.
              </p>
            </div>
          </div>
          
          {/* Unique Value Proposition */}
          <div className="card-professional p-8 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200">
            <h3 className="text-2xl font-bold text-center text-gray-900 mb-6">
              What Makes My Approach Different
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="text-2xl mr-3">📈</span>
                  Business Marketing Foundation
                </h4>
                <p className="text-gray-700 text-sm">
                  My Penn State marketing degree and Enterprise Rent-A-Car management experience give me 
                  unique insight into customer psychology, conversion optimization, and business metrics 
                  that most designers lack.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="text-2xl mr-3">🤝</span>
                  Real Customer Experience
                </h4>
                <p className="text-gray-700 text-sm">
                  Current role at 24 Hour Fitness keeps me connected to real customers daily. 
                  I understand the gap between designed experiences and real-world user behavior 
                  because I see it firsthand.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="heading-section text-center mb-16">
            6-Step Design Process
          </h2>
          
          <div className="space-y-16">
            {processSteps.map((step, index) => (
              <div
                key={step.id}
                className={`flex flex-col lg:flex-row items-center gap-12 ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Step Content */}
                <div className="flex-1">
                  <div className="card-professional p-8 bg-white">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mr-4">
                        {step.number}
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{step.title}</h3>
                        <p className="text-blue-600 font-medium">{step.timeframe}</p>
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-6 text-lg leading-relaxed">
                      {step.description}
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Methods & Activities</h4>
                        <ul className="space-y-2">
                          {step.methods.map((method, methodIndex) => (
                            <li key={methodIndex} className="text-gray-600 text-sm flex items-center">
                              <span className="text-blue-500 mr-2">•</span>
                              {method}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Key Deliverables</h4>
                        <ul className="space-y-2">
                          {step.deliverables.map((deliverable, delivIndex) => (
                            <li key={delivIndex} className="text-gray-600 text-sm flex items-center">
                              <span className="text-green-500 mr-2">✓</span>
                              {deliverable}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Step Icon */}
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-6xl">
                    {step.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Benefits */}
      <section className="section-padding bg-white">
        <div className="container-narrow">
          <h2 className="heading-section text-center mb-12">
            Why This Process Works
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-professional p-6 border-l-4 border-green-500">
              <h3 className="text-xl font-bold text-gray-900 mb-4">🎯 Reduces Risk</h3>
              <p className="text-gray-700">
                By validating assumptions early and often, we minimize the risk of building 
                the wrong thing. Testing and iteration prevent costly mistakes.
              </p>
            </div>
            
            <div className="card-professional p-6 border-l-4 border-blue-500">
              <h3 className="text-xl font-bold text-gray-900 mb-4">⚡ Accelerates Development</h3>
              <p className="text-gray-700">
                Clear requirements, detailed specifications, and validated designs help 
                development teams build faster with fewer revisions.
              </p>
            </div>
            
            <div className="card-professional p-6 border-l-4 border-purple-500">
              <h3 className="text-xl font-bold text-gray-900 mb-4">💰 Maximizes ROI</h3>
              <p className="text-gray-700">
                Focus on measurable business outcomes ensures design decisions contribute 
                to revenue, conversions, and customer satisfaction.
              </p>
            </div>
            
            <div className="card-professional p-6 border-l-4 border-orange-500">
              <h3 className="text-xl font-bold text-gray-900 mb-4">👥 Builds Alignment</h3>
              <p className="text-gray-700">
                Collaborative workshops and clear documentation ensure all stakeholders 
                understand and support the design direction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container-narrow text-center">
          <h2 className="text-3xl font-bold mb-6">
            Ready to Apply This Process to Your Project?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Let&apos;s discuss how my user-centered, business-aware approach can drive 
            results for your team.
          </p>
          <div className="space-y-6">
            <div className="space-y-2">
              <p className="text-lg font-semibold">
                Available for full-time UX roles and consulting projects
              </p>
              <p className="opacity-75">
                New York City • Remote • Hybrid
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DesignProcess;